import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { getDocs, collection } from "firebase/firestore";
import { db, functions } from "../../utils/firebase";
import { collectionPath } from '../../utils/firestoreCollections';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const RegisterForm = ({ onSuccess }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [role, setRole] = useState("");
    const [phone, setPhone] = useState("");
    const [availableRoles, setAvailableRoles] = useState([]);

    useEffect(() => {
        const loadRoles = async () => {
            try {
                const builtIn = [
                    { id: 'admin', name: 'Admin' },
                    { id: 'adminViewOnly', name: 'Admin - View Only' },
                    { id: 'manager', name: 'Manager' },
                    { id: 'marketing', name: 'Marketing' },
                    { id: 'user', name: 'User' },
                ];
                const rolesSnap = await getDocs(collection(db, collectionPath('rolePermissions')));
                const firestoreRoles = rolesSnap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
                // Merge: Firestore overrides built-in names, custom roles appended
                const builtInIds = new Set(builtIn.map(r => r.id));
                const merged = builtIn.map(r => {
                    const override = firestoreRoles.find(fr => fr.id === r.id);
                    return override ? { id: r.id, name: override.name } : r;
                });
                firestoreRoles.forEach(fr => {
                    if (!builtInIds.has(fr.id)) merged.push(fr);
                });
                setAvailableRoles(merged);
            } catch (err) {
                console.error('Failed to load registration roles:', err);
                // Fallback to built-in roles if Firestore fails
                setAvailableRoles([
                    { id: 'admin', name: 'Admin' },
                    { id: 'adminViewOnly', name: 'Admin - View Only' },
                    { id: 'manager', name: 'Manager' },
                    { id: 'marketing', name: 'Marketing' },
                    { id: 'user', name: 'User' },
                ]);
            }
        };
        loadRoles();
    }, []);
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [country, setCountry] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setError("");
        setSuccess("");
        if (currentUser?.role !== 'admin' ||
            currentUser?.permissionDenials?.includes('feature:user_create')) {
            setError('Only admins can create users.');
            return;
        }
        setSaving(true);

        try {
            await httpsCallable(functions, 'createTenantUser')({
                email, password, displayName, firstName, lastName, role, phone,
                address1, address2, city, state, zip, country
            });
            setPassword("");
            if (onSuccess) {
                onSuccess();
            } else {
                setSuccess('User created successfully.');
                navigate("/admin/users");
            }
        } catch (err) {
            console.error('User creation failed:', err.code || 'unknown');
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="mx-auto p-6 bg-white rounded shadow space-y-4 w-full max-w-2xl"
            style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
            <h2 className="text-lg font-semibold mb-2">Add new user</h2>
            {error && <div className="text-red-500">{error}</div>}
            {success && <div className="text-green-600">{success}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Email<span className="text-red-600">*</span></label>
                        <input
                            type="email"
                            className="w-full border p-2 rounded"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Password<span className="text-red-600">*</span></label>
                        <input
                            type="password"
                            minLength={6}
                            className="w-full border p-2 rounded"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">First Name<span className="text-red-600">*</span></label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Last Name<span className="text-red-600">*</span></label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Display Name<span className="text-red-600">*</span></label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={displayName}
                            onChange={e => setDisplayName(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Role<span className="text-red-600">*</span></label>
                        <select
                            className="w-full border p-2 rounded"
                            value={role}
                            onChange={e => setRole(e.target.value)}
                            required
                        >
                            <option value="">Select Role</option>
                            {availableRoles.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Phone</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                        />
                    </div>
                </div>
                {/* Right column */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Address 1</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={address1}
                            onChange={e => setAddress1(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Address 2</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={address2}
                            onChange={e => setAddress2(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">City</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={city}
                            onChange={e => setCity(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">State</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={state}
                            onChange={e => setState(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Zip</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={zip}
                            onChange={e => setZip(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Country</label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded"
                            value={country}
                            onChange={e => setCountry(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <button type="submit" disabled={saving} title="Create user without changing your login"
                className="w-full bg-indigo-600 text-white py-2 rounded mt-4 disabled:opacity-50">
                {saving ? 'Creating...' : 'Register'}
            </button>
        </form>
    );
};

export default RegisterForm;